import { MessageCircle, Phone, MapPin, Clock, Mail } from "lucide-react";
import { COMPANY, buildWhatsAppLink } from "../lib/constants";

const testimonials = [
  {
    quote: "Siempre me llegan mis cajas sin problemas. Los mejores en lo que hacen.",
    author: "María G.",
    avatar:
      "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=120&h=120&fit=crop&crop=faces&q=80",
  },
  {
    quote: "Excelente servicio, rápidos, seguros y confiables.",
    author: "Juan R.",
    avatar:
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=120&h=120&fit=crop&crop=faces&q=80",
  },
];

const contactItems = [
  {
    icon: MessageCircle,
    label: "WhatsApp:",
    value: COMPANY.whatsappDisplay,
    href: buildWhatsAppLink("Hola, me gustaría más información."),
  },
  { icon: Phone, label: "Teléfono:", value: COMPANY.phone2, href: `tel:+1${COMPANY.phone2.replace(/\D/g, "")}` },
  { icon: Phone, label: "Teléfono:", value: COMPANY.phone1, href: `tel:+1${COMPANY.phone1.replace(/\D/g, "")}` },
  { icon: Mail, label: "Email:", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
  { icon: MapPin, label: "", value: COMPANY.address, href: null },
  { icon: Clock, label: "", value: "Lunes a Sábado: 8:00 AM - 6:00 PM", href: null },
];

export default function BottomSection() {
  return (
    <section
      id="contacto"
      data-testid="bottom-section"
      className="bg-[#0B1F4D] text-white"
    >
      {/* Anchor offsets so smooth scroll lands correctly */}
      <div id="sobre-nosotros" className="sr-only" aria-hidden />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* About */}
          <div data-testid="about-column">
            <h3 className="font-display text-lg font-extrabold uppercase tracking-wider">
              Sobre Nosotros
            </h3>
            <div className="mt-2 w-12 h-0.5 bg-[#D61F26]" />
            <p className="mt-5 text-sm text-white/80 leading-relaxed">
              Somos una empresa dedicada a ofrecer servicios de envío de cajas,
              tanques, paquetes y electrodomésticos desde Nueva York hacia
              cualquier parte de República Dominicana.
            </p>
            <p className="mt-3 text-sm text-white/80 leading-relaxed">
              Nuestro compromiso es brindar seguridad, velocidad y confianza en
              cada envío.
            </p>
          </div>

          {/* Testimonials */}
          <div data-testid="testimonials-column">
            <h3 className="font-display text-lg font-extrabold uppercase tracking-wider">
              Testimonios
            </h3>
            <div className="mt-2 w-12 h-0.5 bg-[#D61F26]" />
            <div className="mt-5 space-y-5">
              {testimonials.map((t, i) => (
                <div
                  key={i}
                  data-testid={`testimonial-${i}`}
                  className="flex items-start gap-3"
                >
                  <img
                    src={t.avatar}
                    alt={t.author}
                    className="w-12 h-12 rounded-full object-cover flex-shrink-0 ring-2 ring-white/20"
                  />
                  <div>
                    <p className="text-sm text-white/90 leading-snug italic">
                      &ldquo;{t.quote}&rdquo;
                    </p>
                    <p className="mt-1 text-xs text-[#D61F26] font-bold">
                      — {t.author}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div data-testid="contact-column">
            <h3 className="font-display text-lg font-extrabold uppercase tracking-wider">
              Contáctanos
            </h3>
            <div className="mt-2 w-12 h-0.5 bg-[#D61F26]" />
            <ul className="mt-5 space-y-3">
              {contactItems.map((c, i) => {
                const Icon = c.icon;
                const content = (
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#25D366] flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 text-white" strokeWidth={2.5} />
                    </div>
                    <div className="text-sm leading-snug pt-1">
                      {c.label && <span className="text-white/60 mr-1">{c.label}</span>}
                      <span className="text-white font-semibold">{c.value}</span>
                    </div>
                  </div>
                );
                return (
                  <li key={i} data-testid={`contact-item-${i}`}>
                    {c.href ? (
                      <a href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
                        {content}
                      </a>
                    ) : (
                      content
                    )}
                  </li>
                );
              })}
            </ul>

            <a
              data-testid="contact-whatsapp-btn"
              href={buildWhatsAppLink("Hola, quiero cotizar un envío a República Dominicana.")}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#D61F26] text-white px-5 py-3 uppercase font-bold tracking-wider text-xs hover:bg-[#A3161C] transition-all rounded-md"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={2.5} />
              Habla por WhatsApp
            </a>
          </div>

          {/* Map */}
          <div data-testid="map-column">
            <div className="border-2 border-white/10 overflow-hidden bg-white/5 h-full min-h-[280px]">
              <iframe
                data-testid="google-map"
                title="Ubicación San José Shipping LLC"
                src="https://www.google.com/maps?q=415+E+176+St,+Bronx,+NY+10457&output=embed"
                className="w-full h-full min-h-[280px]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
